import moment from 'moment'
import {
  getWeekNumberAndYear,
  dropTimeZoneFromDate,
  simplerGetTimeOfDayFromDate,
  padZero,
} from '../../shared/libraries/utils/date'
import { normalizeGraphQLConnection } from '../../shared/graphql/utils'
import { filterCompletedMissions } from '../StatsOverview/util'
import { getMetricResultForMissions } from '../../Reducers/MetricResults/selectors'
import { CAMPAIGN_ENUM_TYPES } from '../../shared/constants'
import { getFeatureFromOverlay } from '../BuilderMap/util'
import { getPostcodesForBoundaries } from '../../Utils/Map'
import { isPathFinderSupportsCountry } from '../../shared/libraries/geo'

const calculateTotalScansInMissions = missions =>
  missions.reduce((acc, { amountScans }) => acc + amountScans, 0)

/**
 * @typedef GroupedMission
 * @property {string} primaryLegendLabel The label that matches the primary
 * legend
 * @property {number} totalFlyersDistributed The total flyers distributed for
 * this mission group
 * @property {object} totalMetricResults The metric results for this mission
 * group. To calculate this you can use the getMetricResultForMissions method
 * @property {number} totalMissions The total number of missions in this
 * mission group
 * @property {number} hardCost The total cost of these missions
 *
 * @typedef CalculatedMissionMetrics
 * @property {Array<StatsLegend>} legend
 * @property {Array<object>} data Objects that related to the grouped mission
 * ids as data points with the keys matching the returned legend
 * @property {object} totals Same as data objects but one for all the mission
 * ids TODO: Maybe not return this but call the same function over all
 * mission ids
 *
 * Organises data into a format the can be rendered more easily.
 * @param {StatsLegend} primaryLegend
 * e.g. { label: 'Location', key: 'location' }
 * @param {Array<GroupedMission>} groupedMissions
 * @param {Array<Metric>} metrics
 *
 * @returns {CalculatedMissionMetrics}
 */
export const calculateData = ({
  primaryLegend,
  groupedMissions,
  metrics,
  opt_currencySymbol,
  additionalLegends,
}) => {
  const legend = [
    primaryLegend,
    { label: 'Missions', key: 'missions' },
    { label: 'Flyers Distributed', key: 'flyersDistributed' },
    { label: 'Hard Cost', key: 'hardCost', type: 'currency' },
  ]

  if (additionalLegends) {
    legend.push(...additionalLegends)
  }

  // Show the first metric in the table
  metrics.forEach((metric, i) => {
    const { id, name } = metric
    legend.push({ label: name, key: 'metric-' + id })
    legend.push({
      label: name + ' CVR',
      key: 'metric-' + id + '-cvr',
      // Legacy support
      showAsPercent: true,
      type: 'percent',
    })
    //if (missionCost) {
    legend.push({
      label:
        'Cost per ' +
        name +
        (opt_currencySymbol ? ` ${opt_currencySymbol}` : ''),
      key: 'metric-' + id + '-cps',
      type: 'currency',
    })
    //}
  })

  //const data = groupedMissions.map(
  //  convertGroupedMissionToDto.bind(null, primaryLegend.key, metrics, missionCost)
  //)

  const data = []
  // We reduce so we can create an object that groups all the mission
  // results together to calculate totals
  const totalGroupedMissions = groupedMissions.reduce(
    (prev, cur) => {
      prev.totalFlyersDistributed += cur.totalFlyersDistributed
      prev.totalMissions += cur.totalMissions
      prev.hardCost += cur.hardCost
      prev.scans += cur.scans
      Object.keys(cur.totalMetricResults).forEach(key => {
        if (!(key in prev.totalMetricResults)) prev.totalMetricResults[key] = 0
        prev.totalMetricResults[key] += cur.totalMetricResults[key]
      })
      data.push(convertGroupedMissionToDto(primaryLegend.key, metrics, cur))
      return prev
    },
    {
      totalFlyersDistributed: 0,
      totalMissions: 0,
      hardCost: 0,
      scans: 0,
      totalMetricResults: {},
      primaryLegendLabel: 'Total',
    },
  )
  return {
    legend,
    data,
    totals: convertGroupedMissionToDto(
      primaryLegend.key,
      metrics,
      totalGroupedMissions,
    ),
  }
}

/**
 * Converts the grouped mission data into a data point that contains the
 * information that is commonly sought after. i.e. CVR, CPS and totals
 * @param {} param0
 * @param {*} primaryLegendKey
 * @param {*} missionCost
 */
export const convertGroupedMissionToDto = (
  primaryLegendKey,
  metrics,
  {
    primaryLegendLabel,
    totalFlyersDistributed,
    totalMetricResults,
    totalMissions,
    hardCost,
    scans,
  },
  options = {},
) => {
  const { prefix = 'metric-', metricIdProp = 'id' } = options
  const dto = {
    [primaryLegendKey]: primaryLegendLabel,
    'missions': totalMissions,
    'flyersDistributed': totalFlyersDistributed,
    'hardCost': hardCost / 100,
    'scans': scans,
  }
  metrics.forEach(item => {
    const { id } = item
    dto[prefix + item[metricIdProp]] = totalMetricResults[id] || 0
    dto[prefix + item[metricIdProp] + '-cvr'] =
      (totalMetricResults[id] || 0) / (totalFlyersDistributed || 1)
    if (hardCost != null) {
      dto[prefix + item[metricIdProp] + '-cps'] = totalMetricResults[id]
        ? hardCost / totalMetricResults[id] / 100
        : 0
    }
  })
  return dto
}

/**
 * Calculates printing cost for mission based on printingCostPerFlyer and totalFlyersDistributed
 * @param {Object} mission The Mission that printing cost will be calculated for
 * @return {Number} Printing cost for mission
 * @example
 * const mission = {
 *  printingCostPerFlyer: 0.01,
 *  totalFlyersDistributed: 600,
 * }
 * calculatePrintingCostForMission(mission)
 */
export const calculatePrintingCostForMission = mission => {
  validateHasProps(
    mission,
    ['totalFlyersDistributed', 'printingCostPerFlyer'],
    'mission',
  )
  if (mission.totalFlyersDistributed > 0) {
    return mission.printingCostPerFlyer * mission.totalFlyersDistributed
  }
  return 0
}

/**
 * Calculates the total hard cost for a group of missions
 *
 * @param {object} hashMaps
 * @param {object} hashMaps.cityPlans
 * @param {object} hashMaps.conversions
 * @param {object} hashMaps.metrics
 * @param {object} hashMaps.missions
 * @param {object} hashMaps.permitCosts
 * @param {object} hashMaps.locations
 */
export const calculateHardCost = function(missions, cityPlans) {
  return missions.reduce((acc, mission) => {
    return (
      acc +
      Number(mission.distributionCost) +
      calculatePermitCostForMission(mission, cityPlans) +
      calculatePrintingCostForMission(mission) +
      calculateBonusCostForMission(mission, cityPlans)
    )
  }, 0)
}

/**
 * Utility function that will throw if the object doesn't have the required
 * properties.
 * @param {object} obj The object being evaluated
 * @param {Array<string>} props The required props
 * @param {string} opt_objDescription Optionally make the error message more
 * helpful by stating what the object is.
 */
const validateHasProps = function(obj, props, opt_objDescription) {
  props.forEach(p => {
    if (obj[p] === undefined)
      throw new Error(
        `Missing prop '${p}' ${
          opt_objDescription ? 'on ' + opt_objDescription : ''
        }.`,
      )
  })
}

/**
 * Calculate the bonus cost for a mission, computed from redux state props.
 * @param {string} missionId
 * @param {object} hashMaps
 * @returns {number}
 */
export const calculateBonusCostForMission = mission => {
  const { conversions } = mission
  // Filter out those conversions which won't be paid
  if (mission.bonusPerConversion && mission.bonusRedemptionPeriod) {
    const cutOff = new Date(mission.dateTime)
    cutOff.setDate(cutOff.getDate() + mission.bonusRedemptionPeriod)
    const filteredConversions = conversions.nodes.filter(conversion => {
      const { metric } = conversion
      return metric.isPrimary && conversion.timestamp < cutOff.toISOString()
    })
    return mission.bonusPerConversion * filteredConversions.length
  } else {
    return 0
  }
}
/**
 * Calculates permit cost for each mission based on:
 * If campaign has permit cost set
 * If mission needs permit
 * If that week contains any missions that need a permit
 *
 * @param {number|string} missionId
 * @param {HashMap} hashMaps.missions - The mission that permit cost will be calculated for
 * @param {HashMap} hashMaps.cityPlan - City plan in order to
 * calculate how much missions needs permit cost in this roster
 * @param {HashMap} hashMaps.permitCosts - All permit costs
 * @param {HashMap} hashMaps.missions - HashMap of missions in order to get mission object
 * when looping over city plan missions to count missions that need permit cost
 * @param {HashMap} hashMaps.locations - HashMap of locations in order to get mission's location
 *
 * @return {Number} - Permit cost for the mission
 *
 * @example
 *
 * const hashMaps = {
 *   cityPlans: {
 *     2: {
 *       permitCostIds:[ "1111", "2222", ],
 *       missionIds:[ "1111", "2222", ]
 *     },
 *   locations: {
 *     32: {
 *       hasPermit: true,
 *     },
 *   },
 *   missions: {
 *     1111: {
 *       id: 1,
 *       location: 44,
 *       dateTime: '2019-07-21T10:00:00+10:00',
 *     },
 *   },
 *   permitCosts: {
 *     1111: {
 *       startDate: '2019-07-21T10:00:00+10:00',
 *       cost: 44,
 *     },
 *   }
 * }
 *
 * calculatePermitCostForMission(1, hashMaps)
 *
 */
export const calculatePermitCostForMission = (mission, cityPlans) => {
  const cityPlan = cityPlans.find(({ id }) => id === mission.cityPlanId)
  if (cityPlan.permitCosts.length === 0) {
    return 0
  }

  const missionWeekNumber = moment(mission.dateTime).isoWeek()

  const permitCostsByWeekMap = cityPlan.permitCosts.nodes.reduce(
    (accMap, costItem) => {
      validateHasProps(costItem, ['startDate', 'cost'], 'permit cost')
      const dateObj = new Date(costItem.startDate)
      const weekNumber = moment(dateObj).week()
      accMap.set(weekNumber, {
        permitCost: costItem.cost,
      })
      return accMap
    },
    new Map(),
  )

  if (
    !permitCostsByWeekMap.get(missionWeekNumber) ||
    permitCostsByWeekMap.get(missionWeekNumber).permitCost === 0
  ) {
    return 0
  }

  const missionsByWeekMap = normalizeGraphQLConnection(
    cityPlan.missions,
  ).reduce((accMap, missionItem) => {
    validateHasProps(missionItem, ['dateTime'], 'mission')
    const weekNumber = moment(missionItem.dateTime).isoWeek()
    const prevRecord = accMap.get(weekNumber)
    if (!prevRecord) {
      accMap.set(weekNumber, {
        permitCost: 0,
        missions: [missionItem],
      })
    } else {
      const prevMissions = prevRecord.missions ? prevRecord.missions : []
      accMap.set(weekNumber, {
        ...prevRecord,
        missions: [...prevMissions, missionItem],
      })
    }

    return accMap
  }, permitCostsByWeekMap)

  const {
    permitCost: weekPermitCostCents,
    missions: weekMissions,
  } = missionsByWeekMap.get(missionWeekNumber)

  const weekPermitCost = weekPermitCostCents
  const weekMissionsAmount = weekMissions.length
  const amountOfMissionsWithPermit = weekMissions.filter(mission => {
    const { location } = mission
    validateHasProps(location, ['hasPermit'], 'location')
    return location.hasPermit
  }).length

  const { location } = mission
  validateHasProps(location, ['hasPermit'], 'location')
  if (amountOfMissionsWithPermit === 0) {
    return weekPermitCost / weekMissionsAmount
  } else if (location.hasPermit && weekPermitCost > 0) {
    return weekPermitCost / amountOfMissionsWithPermit
  }

  return 0
}
export const mapReduxStateToHashMaps = state => ({
  ...state,
  missions: state.missions.missions,
  locations: state.locations.locations,
})

/**
 * Determine if the mission should be filtered out or not based on the
 * selected filters
 * @param {object} mission
 * @returns {boolean} Returns true if the mission should not be included
 * in the results
 */
const shouldFilterMission = (mission, cityPlan, filterState) => {
  const missionDate = new Date(dropTimeZoneFromDate(mission.dateTime))

  if (filterState && filterState.timeOfDay) {
    const { city } = cityPlan

    const missionTime = `${padZero(missionDate.getHours())}:${padZero(
      missionDate.getMinutes(),
    )}:${padZero(missionDate.getSeconds())}`
    const isMorning = missionTime < city.lunchCheckInTime
    const isLunch =
      missionTime >= city.lunchCheckInTime &&
      missionTime < city.afternoonCheckInTime
    const isAfternoon = missionTime >= city.afternoonCheckInTime
    if (isMorning && filterState.timeOfDay.includes('MORNING') == false)
      return true
    if (isLunch && filterState.timeOfDay.includes('LUNCH') == false) return true
    if (isAfternoon && filterState.timeOfDay.includes('AFTERNOON') == false)
      return true
  }

  if (filterState && filterState.weeks) {
    const yearAndWeekTuples = filterState.weeks.map(s => s.split('-'))
    if (isDateWithinWeeks(missionDate, yearAndWeekTuples) == false) return true
  }

  // If we have a filter apply that filter
  if (filterState && filterState.locations) {
    if (
      !filterState.locations.includes(mission.location && mission.location.id)
    )
      return true
  }

  // If we have a filter apply that filter
  if (filterState && filterState.brandAmbassadors) {
    if (!filterState.brandAmbassadors.includes(mission.user && mission.user.id))
      return true
  }
  // If we have a filter apply that filter
  if (filterState && filterState.postCodes) {
    return true
  }
  return false
}

/**
 * Checks if a given date is withing an range of weeks
 * @param {Date} date
 * @param {Array<string, string>} yearAndWeekTuples An array of tuples where
 * the first value is the full year and the second value is the week number
 * @returns {boolean}
 */
const isDateWithinWeeks = function(date, yearAndWeekTuples) {
  const { weekNumber, year } = getWeekNumberAndYear(date)
  return yearAndWeekTuples.some(([localYear, localWeekNumber]) => {
    return year == localYear && localWeekNumber == weekNumber
  })
}

export const formatData = (campaign, props) => {
  const { cityPlanId, filterState, locationData } = props
  const campaignData = campaign

  const {
    cityPlans: { nodes: cityPlans },
    metrics: { nodes: metrics },
  } = campaignData

  const cityPlan = cityPlans.find(({ id }) => id === cityPlanId)

  const cityPlanMissions = normalizeGraphQLConnection(cityPlan.missions).map(
    mission => ({ ...mission, cityPlanId: cityPlan.id }),
  )

  const noopData = {
    legend: [],
    data: [],
    totals: {},
  }

  if (!campaignData) {
    return {
      locationData: noopData,
      brandAmbassadorData: noopData,
      dayOfWeekData: noopData,
      postcodesData: noopData,
    }
  }

  const primaryMetric = metrics.find(m => m.isPrimary)
  const locationScatterChartAxisKeys = primaryMetric
    ? [`flyersDistributed`, `metric-${primaryMetric.id}-cvr`]
    : null

  const brandAmbassadorData = calculateData({
    primaryLegend: {
      key: 'brandAmbassador',
      label: 'Brand Ambassador',
    },
    groupedMissions: (() => {
      const missionsGroupedByBA = {}
      cityPlanMissions.filter(filterCompletedMissions).forEach(mission => {
        if (shouldFilterMission(mission, cityPlan, filterState)) return
        if (mission.user) {
          missionsGroupedByBA[mission.user.id] =
            missionsGroupedByBA[mission.user.id] || []
          missionsGroupedByBA[mission.user.id].push(mission)
        }
      })

      return Object.keys(missionsGroupedByBA).map(userId => {
        const missions = missionsGroupedByBA[userId]

        const user = missions[0].user || {
          firstName: '',
          lastName: '',
        }

        return {
          primaryLegendLabel: user.firstName + ' ' + user.lastName,
          ...getMetricResultForMissions(missions),
          totalMissions: missions.length,
          hardCost: calculateHardCost(missions, cityPlans),
          scans: calculateTotalScansInMissions(missions),
        }
      })
    })(),
    metrics,
    additionalLegends: [{ label: 'Scans', key: 'scans' }],
  })

  const dayOfWeekData = calculateData({
    primaryLegend: {
      key: 'dayOfWeek',
      label: 'Day',
    },
    groupedMissions: (() => {
      const missionsGroupedByDay = {
        'Monday': [],
        'Tuesday': [],
        'Wednesday': [],
        'Thursday': [],
        'Friday': [],
        'Saturday': [],
        'Sunday': [],
      }
      cityPlanMissions.filter(filterCompletedMissions).forEach(mission => {
        if (shouldFilterMission(mission, cityPlan, filterState)) return

        const date = new Date(dropTimeZoneFromDate(mission.dateTime))

        missionsGroupedByDay[
          {
            0: 'Sunday',
            1: 'Monday',
            2: 'Tuesday',
            3: 'Wednesday',
            4: 'Thursday',
            5: 'Friday',
            6: 'Saturday',
          }[date.getDay()]
        ].push(mission)
      })

      return Object.keys(missionsGroupedByDay).map(day => {
        const missions = missionsGroupedByDay[day]
        return {
          primaryLegendLabel: day,
          ...getMetricResultForMissions(missions),
          totalMissions: missions.length,
          hardCost: calculateHardCost(missions, cityPlans),
          scans: calculateTotalScansInMissions(missions),
        }
      })
    })(),
    metrics,
    additionalLegends: [{ label: 'Scans', key: 'scans' }],
  })

  const timeOfDayData = calculateData({
    primaryLegend: {
      key: 'timeOfDay',
      label: 'Time of Day',
    },
    groupedMissions: (() => {
      const missionsGroupedByTimeOfDay = {
        'Morning': [],
        'Lunch': [],
        'Afternoon': [],
      }
      cityPlanMissions.filter(filterCompletedMissions).forEach(mission => {
        if (shouldFilterMission(mission, cityPlan, filterState)) return
        const { city } = cityPlan
        let timeOfDay = simplerGetTimeOfDayFromDate(
          dropTimeZoneFromDate(mission.dateTime),
          city,
        ).toLowerCase()
        timeOfDay = timeOfDay.slice(0, 1).toUpperCase() + timeOfDay.slice(1)
        missionsGroupedByTimeOfDay[timeOfDay].push(mission)
      })

      return Object.keys(missionsGroupedByTimeOfDay).map(day => {
        const missions = missionsGroupedByTimeOfDay[day]
        return {
          primaryLegendLabel: day,
          ...getMetricResultForMissions(missions),
          totalMissions: missions.length,
          hardCost: calculateHardCost(missions, cityPlans),
          scans: calculateTotalScansInMissions(missions),
        }
      })
    })(),
    metrics,
    additionalLegends: [{ label: 'Scans', key: 'scans' }],
  })

  const filteredLocationDataByWeek = locationData.data.filter(item => {
    if (filterState && filterState.weeks) {
      const yearAndWeekTuples = filterState.weeks.map(s => s.split('-'))
      if (
        !item.missions.some(i =>
          isDateWithinWeeks(i.dateTime, yearAndWeekTuples),
        )
      )
        return false
    }
    if (filterState.locations && filterState.locations.length) {
      const locationId =
        item && item.missions.length ? item.missions[0].location.id : null
      return filterState.locations.includes(locationId)
    }
    if (!filterState.postcodes || !filterState.postcodes.length) return true
    return filterState.postcodes.includes(item.location)
  })

  const locationDataProcessed = {
    title: 'Locations',
    data: calculateData({
      primaryLegend: {
        key: 'location',
        label: 'Location',
      },
      groupedMissions: filteredLocationDataByWeek,
      metrics,
      additionalLegends: [{ label: 'Scans', key: 'scans' }],
    }),
  }
  return {
    locationScatterChartAxisKeys,
    brandAmbassadorData,
    dayOfWeekData,
    timeOfDayData,
    locationDataProcessed,
  }
}
export const getLocationsData = async (campaign, props) => {
  const { cityPlanId, filterState } = props

  const {
    cityPlans: { nodes: cityPlans },
  } = campaign

  const cityPlan = cityPlans.find(({ id }) => id === cityPlanId)

  const countryCode = cityPlan.city.country.code

  const cityPlanMissions = normalizeGraphQLConnection(cityPlan.missions).map(
    mission => ({ ...mission, cityPlanId: cityPlan.id }),
  )

  const prepareLocationsData = async () => {
    const missionsGroupedByLocation = {}
    const filteredMissions = cityPlanMissions.filter(filterCompletedMissions)
    if (
      campaign.type === CAMPAIGN_ENUM_TYPES.LETTERBOX &&
      isPathFinderSupportsCountry(countryCode)
    ) {
      try {
        const geometries = filteredMissions.map(
          ({ boundary }) =>
            getFeatureFromOverlay({ countryCode, boundary }).geometry,
        )

        const relatedPostcodesData = await getPostcodesForBoundaries({
          geometries,
          countryCode,
        })

        relatedPostcodesData.forEach((postcode, index) => {
          const name = postcode || 'Other'
          missionsGroupedByLocation[name] =
            missionsGroupedByLocation[name] || []
          missionsGroupedByLocation[name].push(filteredMissions[index])
        })
      } catch (error) {
        console.error({ error })
      }
    }

    filteredMissions.forEach(mission => {
      if (shouldFilterMission(mission, cityPlan, filterState)) return
      if (mission.location) {
        missionsGroupedByLocation[mission.location.name] =
          missionsGroupedByLocation[mission.location.name] || []
        missionsGroupedByLocation[mission.location.name].push(mission)
      }
    })
    return Object.keys(missionsGroupedByLocation).map(locationName => {
      const missions = missionsGroupedByLocation[locationName]
      return {
        primaryLegendLabel: locationName,
        ...getMetricResultForMissions(missions),
        totalMissions: missions.length,
        hardCost: calculateHardCost(missions, cityPlans),
        missions: missions,
        scans: calculateTotalScansInMissions(missions),
      }
    })
  }

  const preparedLocationsData = await prepareLocationsData()

  return {
    title:
      campaign.type === CAMPAIGN_ENUM_TYPES.HANDTOHAND
        ? 'Locations'
        : 'Postcodes',
    data: preparedLocationsData,
  }
}



// WEBPACK FOOTER //
// ./src/Containers/StatsCityPlan/util.js